// frontend/src/components/ParkingDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ExcelJS from 'exceljs';
import { parkingAPI } from '../services/api';
import './styles/ParkingDashboard.css';

const MESES = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
];

export default function ParkingDashboard() {
    const today = new Date();
    const [period, setPeriod] = useState('q1');
    const [selectedYear, setSelectedYear] = useState(today.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const getFechasPeriodo = useCallback(() => {
        const monthStr = String(selectedMonth).padStart(2, '0');
        if (period === 'q1') {
            return {
                start: `${selectedYear}-${monthStr}-01`,
                end: `${selectedYear}-${monthStr}-15`,
                label: `1ra Quincena (1 al 15 de ${MESES.find(m => m.value === selectedMonth)?.label} ${selectedYear})`
            };
        } else if (period === 'q2') {
            const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
            return {
                start: `${selectedYear}-${monthStr}-16`,
                end: `${selectedYear}-${monthStr}-${lastDay}`,
                label: `2da Quincena (16 al ${lastDay} de ${MESES.find(m => m.value === selectedMonth)?.label} ${selectedYear})`
            };
        } else if (period === 'month') {
            const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
            return {
                start: `${selectedYear}-${monthStr}-01`,
                end: `${selectedYear}-${monthStr}-${lastDay}`,
                label: `Mes Completo (${MESES.find(m => m.value === selectedMonth)?.label} ${selectedYear})`
            };
        }
        return { start: null, end: null, label: period };
    }, [period, selectedMonth, selectedYear]);

    const loadStats = useCallback(async () => {
        setLoading(true);
        try {
            let params = { period };
            if (['q1', 'q2', 'month'].includes(period)) {
                const fechas = getFechasPeriodo();
                params = { period: 'custom', startDate: fechas.start, endDate: fechas.end };
            }

            const res = await parkingAPI.getStats(params);
            if (res.data?.success) {
                setStats(res.data.data);
            }
        } catch (error) {
            console.error('Error al cargar stats de parking:', error);
        } finally {
            setLoading(false);
        }
    }, [period, getFechasPeriodo]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    const periodLabels = {
        q1: 'Q1 (1-15)',
        q2: 'Q2 (16-Fin)',
        today: 'Hoy',
        week: 'Esta Semana',
        month: 'Mes Completo',
        year: 'Este Año'
    };

    // ── Exportación a Excel del Cierre Quincenal y Flujo de Caja ──
    const exportarCierreExcel = async () => {
        const fechas = getFechasPeriodo();
        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'Lobos Parking ERP';
        workbook.created = new Date();

        // Hoja 1: Resumen de Cierre de Caja
        const wsResumen = workbook.addWorksheet('Cierre de Caja', {
            views: [{ showGridLines: true }]
        });

        wsResumen.columns = [
            { width: 32 },
            { width: 26 },
            { width: 30 }
        ];

        // Título
        wsResumen.mergeCells('A1:C1');
        const titleCell = wsResumen.getCell('A1');
        titleCell.value = 'LOBOS PARKING — CIERRE Y CUADRE DE CAJA';
        titleCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E293B' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        wsResumen.getRow(1).height = 36;

        // Subtítulo período
        wsResumen.mergeCells('A2:C2');
        const subCell = wsResumen.getCell('A2');
        subCell.value = `Período: ${fechas.label} | Generado el: ${new Date().toLocaleString()}`;
        subCell.font = { name: 'Calibri', size: 11, italic: true, color: { argb: 'FF475569' } };
        subCell.alignment = { horizontal: 'center', vertical: 'middle' };
        wsResumen.getRow(2).height = 24;

        wsResumen.addRow([]);

        // Encabezado bloque balance
        const headerRow = wsResumen.addRow(['CONCEPTO FINANCIERO', 'MONTO ($ USD)', 'DETALLE / OBSERVACIÓN']);
        headerRow.height = 26;
        headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        headerRow.alignment = { vertical: 'middle' };
        ['A', 'B', 'C'].forEach(col => {
            wsResumen.getCell(`${col}4`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0284C7' } };
        });

        const recaudado = parseFloat(stats?.recaudado_periodo || 0);
        const gastos = parseFloat(stats?.gastos_periodo || 0);
        const saldoNeto = recaudado - gastos;

        const rowIngresos = wsResumen.addRow(['(+) Ingresos Brutos (Cobros Parking)', recaudado, 'Total cobrado por estadías']);
        rowIngresos.getCell(2).numFmt = '$#,##0.00';
        rowIngresos.font = { bold: true };

        (stats?.pagos_desglose || []).forEach(p => {
            const rowP = wsResumen.addRow([`     • Cobrado en ${p.metodo_pago}`, parseFloat(p.total), 'Cobro directo']);
            rowP.getCell(2).numFmt = '$#,##0.00';
            rowP.font = { italic: true, color: { argb: 'FF475569' } };
        });

        const rowGastos = wsResumen.addRow(['(-) Gastos Operativos (Combustible, etc.)', gastos, 'Salidas de caja registradas']);
        rowGastos.getCell(2).numFmt = '$#,##0.00';
        rowGastos.font = { bold: true, color: { argb: 'FFDC2626' } };

        wsResumen.addRow([]);

        const rowSaldo = wsResumen.addRow(['(=) SALDO NETO EN CAJA', saldoNeto, 'Dinero disponible post-gastos']);
        rowSaldo.height = 28;
        rowSaldo.font = { bold: true, size: 13, color: { argb: saldoNeto >= 0 ? 'FF15803D' : 'FFDC2626' } };
        rowSaldo.getCell(2).numFmt = '$#,##0.00';
        ['A', 'B', 'C'].forEach(col => {
            wsResumen.getCell(`${col}${rowSaldo.number}`).fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: saldoNeto >= 0 ? 'FFDCFCE7' : 'FFFEE2E2' }
            };
        });

        // Hoja 2: Detalle de Cobros
        const wsCobros = workbook.addWorksheet('Cobros Realizados');
        wsCobros.columns = [
            { header: 'Placa', key: 'placa', width: 14 },
            { header: 'Cliente', key: 'cliente', width: 26 },
            { header: 'Días', key: 'dias', width: 10 },
            { header: 'Fecha Salida', key: 'fecha_salida', width: 18 },
            { header: 'Monto Total ($)', key: 'monto', width: 16 },
            { header: 'Método(s) Pago', key: 'metodos', width: 22 }
        ];
        wsCobros.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
        wsCobros.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF059669' } };

        (stats?.ultimos_cobros || []).forEach(c => {
            const row = wsCobros.addRow({
                placa: c.placa,
                cliente: c.cliente_nombre,
                dias: c.dias_cobrados,
                fecha_salida: c.fecha_salida ? new Date(c.fecha_salida).toLocaleString() : '-',
                monto: parseFloat(c.monto_total || 0),
                metodos: (c.pagos || []).map(p => `${p.metodo_pago}: $${parseFloat(p.monto).toFixed(2)}`).join(' + ') || 'USD'
            });
            row.getCell(5).numFmt = '$#,##0.00';
        });

        // Hoja 3: Detalle de Gastos
        const wsGastos = workbook.addWorksheet('Gastos Operativos');
        wsGastos.columns = [
            { header: 'Concepto de Gasto', key: 'desc', width: 34 },
            { header: 'Fecha Consumo', key: 'fecha', width: 16 },
            { header: 'Monto ($ USD)', key: 'monto', width: 16 }
        ];
        wsGastos.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
        wsGastos.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDC2626' } };

        (stats?.gastos_detalle || []).forEach(g => {
            const row = wsGastos.addRow({
                desc: g.descripcion,
                fecha: g.fecha_consumo ? new Date(g.fecha_consumo).toLocaleDateString('es-PA', { timeZone: 'UTC' }) : '-',
                monto: parseFloat(g.monto || 0)
            });
            row.getCell(3).numFmt = '$#,##0.00';
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `Cierre_Quincenal_Lobos_Parking_${selectedYear}_M${selectedMonth}_${period}.xlsx`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
    };

    const ingresosBrutos = parseFloat(stats?.recaudado_periodo || 0);
    const gastosOperativos = parseFloat(stats?.gastos_periodo || 0);
    const saldoNeto = ingresosBrutos - gastosOperativos;
    const fechasInfo = getFechasPeriodo();

    return (
        <div className="parking-dash-container">
            {/* Header */}
            <div className="parking-dash-header">
                <div className="parking-dash-title">
                    <h1>📊 Cierre Quincenal y Flujo de Caja — Lobos Parking</h1>
                    <p className="parking-dash-subtitle">
                        {fechasInfo.label} • Control de Ingresos, Gastos Operativos y Saldo de Caja
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Selector de Quincena y Períodos */}
                    <div className="parking-dash-period-pills">
                        {Object.entries(periodLabels).map(([key, label]) => (
                            <button
                                key={key}
                                className={`period-pill ${period === key ? 'active' : ''}`}
                                onClick={() => setPeriod(key)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* Selector Mes / Año si es quincenal o mensual */}
                    {['q1', 'q2', 'month'].includes(period) && (
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', background: '#f1f5f9', padding: '4px 8px', borderRadius: '8px' }}>
                            <select 
                                value={selectedMonth} 
                                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                                style={{ border: 'none', background: 'transparent', fontWeight: 600, color: '#334155', cursor: 'pointer', fontSize: '13px' }}
                            >
                                {MESES.map(m => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </select>
                            <select 
                                value={selectedYear} 
                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                                style={{ border: 'none', background: 'transparent', fontWeight: 600, color: '#334155', cursor: 'pointer', fontSize: '13px' }}
                            >
                                <option value={2025}>2025</option>
                                <option value={2026}>2026</option>
                                <option value={2027}>2027</option>
                            </select>
                        </div>
                    )}

                    <button 
                        className="period-pill" 
                        style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '6px', background: '#0284c7', color: '#ffffff', fontWeight: 600 }}
                        onClick={exportarCierreExcel}
                        title="Descargar reporte completo en Excel con detalle de ingresos y gastos"
                    >
                        📥 Exportar Excel
                    </button>

                    <button 
                        className="period-pill" 
                        style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '6px', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca' }}
                        onClick={() => navigate('/expenses')}
                    >
                        💸 Gastos
                    </button>

                    <button 
                        className="period-pill active" 
                        style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '6px' }}
                        onClick={() => navigate('/parking')}
                    >
                        🅿️ Ir al Patio
                    </button>
                </div>
            </div>

            {/* KPIs Principales: Ingresos -> Gastos -> Saldo de Caja */}
            <div className="parking-dash-kpis">
                <div className="dash-kpi-card">
                    <div className="dash-kpi-icon dash-icon-purple">💵</div>
                    <div className="dash-kpi-details">
                        <span className="dash-kpi-label">Ingresos Brutos ({periodLabels[period] || period})</span>
                        <span className="dash-kpi-value" style={{ color: '#0284c7' }}>
                            ${ingresosBrutos.toFixed(2)} USD
                        </span>
                        <span className="dash-kpi-sub">
                            {(stats?.pagos_desglose || []).map(p => `${p.metodo_pago}: $${parseFloat(p.total).toFixed(2)}`).join(' • ') || 'Sin cobros registrados'}
                        </span>
                    </div>
                </div>

                <div className="dash-kpi-card">
                    <div className="dash-kpi-icon" style={{ background: '#fef2f2', color: '#ef4444' }}>📉</div>
                    <div className="dash-kpi-details">
                        <span className="dash-kpi-label">Gastos Operativos ({periodLabels[period] || period})</span>
                        <span className="dash-kpi-value" style={{ color: '#ef4444' }}>
                            ${gastosOperativos.toFixed(2)} USD
                        </span>
                        <span className="dash-kpi-sub">Combustible y compras operativas</span>
                    </div>
                </div>

                <div className="dash-kpi-card" style={{ border: saldoNeto >= 0 ? '1px solid #bbf7d0' : '1px solid #fecaca', background: saldoNeto >= 0 ? '#f0fdf4' : '#fff5f5' }}>
                    <div className="dash-kpi-icon" style={{ background: saldoNeto >= 0 ? '#dcfce7' : '#fee2e2', color: saldoNeto >= 0 ? '#15803d' : '#dc2626' }}>💰</div>
                    <div className="dash-kpi-details">
                        <span className="dash-kpi-label">Saldo Neto en Caja ({periodLabels[period] || period})</span>
                        <span className="dash-kpi-value" style={{ color: saldoNeto >= 0 ? '#15803d' : '#dc2626', fontWeight: 800 }}>
                            ${saldoNeto.toFixed(2)} USD
                        </span>
                        <span className="dash-kpi-sub">
                            {saldoNeto >= 0 ? '✓ Ingresos cubren los gastos' : '⚠️ Caja en saldo negativo'}
                        </span>
                    </div>
                </div>

                <div className="dash-kpi-card">
                    <div className="dash-kpi-icon dash-icon-blue">🚗</div>
                    <div className="dash-kpi-details">
                        <span className="dash-kpi-label">Vehículos en Patio Ahora</span>
                        <span className="dash-kpi-value">{stats?.vehiculos_en_patio || 0}</span>
                        <span className="dash-kpi-sub">Autos en custodia activa</span>
                    </div>
                </div>

                <div className="dash-kpi-card">
                    <div className="dash-kpi-icon dash-icon-green">📥</div>
                    <div className="dash-kpi-details">
                        <span className="dash-kpi-label">Entradas ({periodLabels[period] || period})</span>
                        <span className="dash-kpi-value">{stats?.entradas_periodo || 0}</span>
                        <span className="dash-kpi-sub">Vehículos recibidos</span>
                    </div>
                </div>

                <div className="dash-kpi-card">
                    <div className="dash-kpi-icon dash-icon-amber">📤</div>
                    <div className="dash-kpi-details">
                        <span className="dash-kpi-label">Salidas ({periodLabels[period] || period})</span>
                        <span className="dash-kpi-value">{stats?.salidas_periodo || 0}</span>
                        <span className="dash-kpi-sub">Vehículos entregados</span>
                    </div>
                </div>
            </div>

            {/* Secciones de Detalle: Cobros y Gastos */}
            <div className="parking-dash-sections">
                {/* Panel Últimos Cobros */}
                <div className="dash-panel">
                    <div className="dash-panel-header">
                        <h3>🧾 Ingresos y Cobros ({periodLabels[period] || period})</h3>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>Cobros de estadía</span>
                    </div>

                    {loading ? (
                        <p style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>Cargando cobros...</p>
                    ) : (stats?.ultimos_cobros || []).length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '30px 20px', color: '#94a3b8' }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>💵</div>
                            <p style={{ margin: 0, fontSize: '13px' }}>No hay cobros registrados en este período.</p>
                        </div>
                    ) : (
                        <div className="dash-list">
                            {(stats?.ultimos_cobros || []).map(item => (
                                <div key={item.id} className="dash-list-item">
                                    <div className="dash-item-left">
                                        <span className="dash-plate-mini" style={{ background: '#059669' }}>{item.placa}</span>
                                        <div className="dash-item-info">
                                            <span className="dash-item-name">{item.cliente_nombre}</span>
                                            <span className="dash-item-sub">
                                                {item.dias_cobrados} días • {new Date(item.fecha_salida).toLocaleDateString()} {new Date(item.fecha_salida).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div className="dash-amount-badge">${parseFloat(item.monto_total).toFixed(2)}</div>
                                        <div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>
                                            {(item.pagos || []).map(p => p.metodo_pago).join(' + ') || 'USD'}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Panel Gastos del Período */}
                <div className="dash-panel">
                    <div className="dash-panel-header">
                        <h3>💸 Gastos Operativos ({periodLabels[period] || period})</h3>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>Salidas de caja</span>
                    </div>

                    {loading ? (
                        <p style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>Cargando gastos...</p>
                    ) : (stats?.gastos_detalle || []).length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '30px 20px', color: '#94a3b8' }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>⛽</div>
                            <p style={{ margin: 0, fontSize: '13px' }}>No se registran gastos operativos en este período.</p>
                        </div>
                    ) : (
                        <div className="dash-list">
                            {(stats?.gastos_detalle || []).map(item => (
                                <div key={item.id} className="dash-list-item">
                                    <div className="dash-item-left">
                                        <span className="dash-plate-mini" style={{ background: '#ef4444' }}>GASTO</span>
                                        <div className="dash-item-info">
                                            <span className="dash-item-name">{item.descripcion}</span>
                                            <span className="dash-item-sub">
                                                📅 {item.fecha_consumo ? new Date(item.fecha_consumo).toLocaleDateString('es-PA', { timeZone: 'UTC' }) : '-'}
                                            </span>
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div className="dash-amount-badge" style={{ color: '#ef4444' }}>
                                            -${parseFloat(item.monto || 0).toFixed(2)}
                                        </div>
                                        <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 700 }}>
                                            USD
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
